import type { Ucs } from "@agentry/zerovia-contracts";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	Field,
	LinkButton,
	PageHeader,
	Select,
	TextInput,
} from "../../ui/primitives";
import { EmptyState, ErrorState, LoadingState, NotFoundState, recoveryFor } from "../../ui/states";
import { filterUcsByAgentFilterList, readAgentFilterValue } from "./agent-filter";
import { friendlyNodeName } from "./ProgressPages";
import { PACK_HEARING_DISPLAY_NAME, PACK_HEARING_UCS_ID, packHearing } from "./pack-hearing";

function endUserAgentName(ucs: Ucs): string {
	return ucs.id === PACK_HEARING_UCS_ID ? PACK_HEARING_DISPLAY_NAME : ucs.displayName;
}

/**
 * Agent選択。
 * End Userには「Tenantで利用が許可されているか」だけを見せ、
 * Provider内部・Workflow内部構造は見せない。
 */
export function AgentSearchPage() {
	const { client } = useApi();
	const scope = useScope();
	const { session } = useAuth();
	const [query, setQuery] = useState("");
	const [category, setCategory] = useState("all");

	const data = useAsync(async () => {
		const userId = session?.user.id;
		const [catalog, entitlements, filterValue] = await Promise.all([
			client.getCatalog(scope),
			client.listCatalogEntitlements(scope),
			userId ? readAgentFilterValue(client, userId) : Promise.resolve(undefined),
		]);
		return {
			catalog: {
				...catalog,
				ucs: filterUcsByAgentFilterList(catalog.ucs, filterValue),
			},
			entitlements,
		};
	}, [client, scope.tenantId, scope.projectId, session?.user.id]);

	const categories = useMemo(() => {
		if (data.status !== "ready") return [];
		return [...new Set(data.data.catalog.ucs.map((u) => u.categoryCode))].sort();
	}, [data]);

	return (
		<>
			<PageHeader
				eyebrow="Agentを探す"
				title="どんな仕事を頼みますか"
				description="業種と目的から選びます。選んだあとはAgentが質問するので、依頼文を考える必要はありません。"
			/>

			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}

			{data.status === "ready" && (
				<>
					<div className="mb-5 flex flex-wrap gap-3">
						<div className="min-w-56 flex-1">
							<TextInput
								type="search"
								placeholder="キーワードで探す（例: LP、調査、飲食）"
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								aria-label="Agentをキーワードで探す"
							/>
						</div>
						<Select
							value={category}
							onChange={(event) => setCategory(event.target.value)}
							aria-label="カテゴリで絞り込む"
							className="w-44"
						>
							<option value="all">すべてのカテゴリ</option>
							{categories.map((code) => (
								<option key={code} value={code}>
									{code}
								</option>
							))}
						</Select>
					</div>

					{(() => {
						const keyword = query.trim().toLowerCase();
						const visible = data.data.catalog.ucs.filter((ucs) => {
							if (ucs.status !== "PUBLISHED") return false;
							if (category !== "all" && ucs.categoryCode !== category) return false;
							if (!keyword) return true;
							return `${ucs.displayName}${ucs.description}${ucs.verticalKey ?? ""}`
								.toLowerCase()
								.includes(keyword);
						});
						if (visible.length === 0) {
							return (
								<EmptyState
									title="条件に合うAgentが見つかりません"
									description="キーワードを短くするか、カテゴリの絞り込みを外してください。"
									action={
										<Button
											size="sm"
											variant="secondary"
											onClick={() => {
												setQuery("");
												setCategory("all");
											}}
										>
											絞り込みを解除
										</Button>
									}
								/>
							);
						}
						return (
							<div className="grid gap-4 lg:grid-cols-2">
								{visible.map((ucs) => {
									const entitlement = data.data.entitlements.find((e) => e.ucsId === ucs.id);
									return (
										<AgentCard
											key={ucs.id}
											ucs={ucs}
											enabled={entitlement?.enabled ?? false}
											restrictedReason={entitlement?.restrictedReason ?? null}
										/>
									);
								})}
							</div>
						);
					})()}
				</>
			)}
		</>
	);
}

function AgentCard({
	ucs,
	enabled,
	restrictedReason,
}: {
	ucs: Ucs;
	enabled: boolean;
	restrictedReason: string | null;
}) {
	const usesPackHearing = ucs.id === PACK_HEARING_UCS_ID;
	return (
		<Card
			title={endUserAgentName(ucs)}
			description={ucs.description}
			action={
				enabled ? (
					<LinkButton to={`/app/agents/${ucs.id}`} size="sm">
						依頼する
					</LinkButton>
				) : (
					<Badge tone="neutral">利用不可</Badge>
				)
			}
		>
			<div className="flex flex-wrap items-center gap-1.5">
				<Badge tone="brand">{ucs.categoryCode}</Badge>
				{ucs.verticalKey && <Badge tone="success">{ucs.verticalKey}</Badge>}
			</div>
			{usesPackHearing && (
				<div className="mt-3 border-t border-ink-100 pt-3">
					<p className="text-[12px] font-bold text-ink-600">最初に聞かれること</p>
					<ul className="mt-1.5 space-y-1 text-[12.5px] text-ink-700">
						{packHearing.questions.slice(0, 3).map((question) => (
							<li key={question.key}>・{question.prompt}</li>
						))}
					</ul>
					<p className="mt-1 text-[11.5px] text-ink-500">
						ほか {packHearing.questions.length - 3} 問
					</p>
				</div>
			)}
			{!enabled && restrictedReason && (
				<p className="mt-2 text-[12.5px] text-ink-500">{restrictedReason}</p>
			)}
		</Card>
	);
}

/** Agent詳細 → Session作成。ここがGuided Chatへの入口。 */
export function AgentDetailPage() {
	const { ucsId = "" } = useParams();
	const { client } = useApi();
	const scope = useScope();
	const { session } = useAuth();
	const navigate = useNavigate();

	const data = useAsync(async () => {
		const userId = session?.user.id;
		const [catalog, entitlements, projects, filterValue] = await Promise.all([
			client.getCatalog(scope),
			client.listCatalogEntitlements(scope),
			client.listProjects(scope),
			userId ? readAgentFilterValue(client, userId) : Promise.resolve(undefined),
		]);
		return {
			catalog: {
				...catalog,
				ucs: filterUcsByAgentFilterList(catalog.ucs, filterValue),
			},
			entitlements,
			projects,
		};
	}, [client, scope.tenantId, scope.projectId, session?.user.id]);

	const [form, setForm] = useState({ title: "", purpose: "", projectId: "" });
	const create = useAction(async () => {
		const created = await client.createSession(scope, {
			projectId: form.projectId || scope.projectId,
			ucsId,
			title: form.title,
			businessPurpose: form.purpose,
			sessionMode: "GUIDED_WORKFLOW",
			securityClassification: "INTERNAL",
		});
		return created;
	});

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const ucs = data.data.catalog.ucs.find((u) => u.id === ucsId);
	if (!ucs) return <NotFoundState detail="このAgentは利用できる一覧にありません。" />;

	const entitlement = data.data.entitlements.find((e) => e.ucsId === ucsId);
	const agent = data.data.catalog.agents.find((a) => a.id === ucs.agentId);
	const pack = data.data.catalog.verticalPackages.find((v) => v.key === ucs.verticalKey);
	const usesPackHearing = ucs.id === PACK_HEARING_UCS_ID;
	const questions = usesPackHearing ? packHearing.questions : (pack?.guidedQuestions ?? []);
	const displayName = endUserAgentName(ucs);
	// The external Catalog response predates workflowDefinitions. Treat the
	// field as additive/optional so a Real response cannot crash the request
	// form while older Gateways are still serving the compact view.
	const workflow = (data.data.catalog.workflowDefinitions ?? []).find(
		(w) => w.key === ucs.workflowDefinitionKey,
	);

	return (
		<>
			<PageHeader eyebrow={ucs.categoryCode} title={displayName} description={ucs.description} />

			{!entitlement?.enabled && (
				<Callout tone="warning" title="このAgentは現在利用できません">
					{entitlement?.restrictedReason ?? "組織で利用が許可されていません。"}
					<p className="mt-1">組織の管理者に利用申請してください。</p>
				</Callout>
			)}

			<div className="mt-5 grid gap-4 lg:grid-cols-[1fr_360px]">
				<div className="space-y-4">
					{questions.length > 0 && (
						<Card
							title="最初に聞かれること"
							description="この順番で質問します。答えは後から変更できます。"
						>
							<ol className="space-y-2.5">
								{questions.map((question, index) => (
									<li key={question.key} className="flex gap-3">
										<span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[11px] font-bold text-ink-500">
											{index + 1}
										</span>
										<div>
											<p className="text-[14px] font-medium text-ink-900">{question.prompt}</p>
											{question.options.length > 0 && (
												<div className="mt-1 flex flex-wrap gap-1">
													{question.options.map((option) => (
														<span
															key={option}
															className="rounded-full bg-ink-100 px-2 py-0.5 text-[11.5px] text-ink-600"
														>
															{option}
														</span>
													))}
												</div>
											)}
											{question.helpText && (
												<p className="mt-0.5 text-[12.5px] text-ink-500">{question.helpText}</p>
											)}
										</div>
									</li>
								))}
							</ol>
						</Card>
					)}

					{workflow && (
						<Card title="進め方" description="この工程は省略できません">
							<ol className="flex flex-wrap gap-1.5">
								{workflow.nodes
									.filter((node) => node.nodeType !== "START" && node.nodeType !== "END")
									.map((node) => (
										<li
											key={node.nodeId}
											className="rounded-full border border-ink-200 px-2.5 py-1 text-[12.5px] text-ink-700"
										>
											{friendlyNodeName(node.nodeId, node.nodeType)}
										</li>
									))}
							</ol>
						</Card>
					)}

					{pack && (
						<Card title="この業種で守ること">
							<ul className="ml-4 list-disc space-y-1 text-[13.5px] text-ink-700">
								{pack.industryKnowledge.map((item) => (
									<li key={item.title}>
										<span className="font-semibold">{item.title}</span>：{item.body}
									</li>
								))}
							</ul>
						</Card>
					)}
				</div>

				<Card title="依頼をはじめる">
					<form
						className="space-y-4"
						onSubmit={async (event) => {
							event.preventDefault();
							const created = await create.run();
							if (created) navigate(`/app/sessions/${created.id}`);
						}}
					>
						<Field label="この依頼の名前" hint="あとから見つけやすい名前をつけてください">
							<TextInput
								value={form.title}
								onChange={(event) => setForm({ ...form, title: event.target.value })}
								placeholder="例: 秋季キャンペーンLP制作"
								required
							/>
						</Field>
						<Field label="プロジェクト">
							<Select
								value={form.projectId || scope.projectId}
								onChange={(event) => setForm({ ...form, projectId: event.target.value })}
							>
								{data.data.projects.map((project) => (
									<option key={project.id} value={project.id}>
										{project.name}
									</option>
								))}
							</Select>
						</Field>
						<Field label="達成したいこと" hint="任意。分かる範囲でかまいません">
							<TextInput
								value={form.purpose}
								onChange={(event) => setForm({ ...form, purpose: event.target.value })}
								placeholder="例: 秋の新規予約を増やす"
							/>
						</Field>

						{create.error != null && (
							<div
								role="alert"
								className="rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
							>
								<p className="text-[13px] font-bold text-danger-600">
									{recoveryFor(create.error).title}
								</p>
								<p className="text-[12.5px] text-ink-700">{recoveryFor(create.error).hint}</p>
							</div>
						)}

						<Button
							type="submit"
							className="w-full"
							disabled={create.pending || !entitlement?.enabled}
						>
							{create.pending ? "準備しています…" : "この内容で相談を始める"}
						</Button>
						<p className="text-[12px] text-ink-500">
							{usesPackHearing
								? `${displayName}が担当します。`
								: agent
									? `${agent.displayName}が担当します。`
									: ""}
							途中でやめても内容は残ります。
						</p>
					</form>
				</Card>
			</div>

			<p className="mt-6 text-[13px]">
				<Link to="/app/agents" className="text-ink-600 hover:text-brand-600">
					← Agent一覧へ戻る
				</Link>
			</p>
		</>
	);
}
